/**
 * Objektliste einer Einrichten-Art — der Zwischenschritt Katalog → Objektblatt.
 *
 * Eine Tabelle für alle Arten: Name, Kürzel (wo vorhanden), Zustand, letzte
 * Änderung. Inaktive sind sichtbar (gedimmt) statt verschluckt — die
 * Einbahnstraße der Alt-Listen ist damit Geschichte.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../App';
import { LeitstandRahmen } from '../../components/leitstand/LeitstandRahmen';
import { Karte, Kopfzelle, Notiz, Tabelle, Zelle, Zustand } from '../../components/leitstand';
import { ARTEN } from './arten';

interface Objekt {
  id: string;
  name: string;
  code?: string;
  isActive: boolean;
  updatedAt: string;
}

export default function ObjektListe() {
  const { user } = useAuth();
  const { art = '' } = useParams<{ art: string }>();
  const navigate = useNavigate();
  const def = ARTEN[art];

  const [objekte, setObjekte] = useState<Objekt[]>([]);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState('');
  const [suchtext, setSuchtext] = useState('');

  const lade = useCallback(async () => {
    if (!def) return;
    setLaden(true);
    setFehler('');
    try {
      const res = await fetch(`/api/einrichten/objekt/${art}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Die Liste konnte nicht geladen werden');
      const body = await res.json();
      setObjekte(body.objekte ?? []);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Die Liste konnte nicht geladen werden');
    } finally {
      setLaden(false);
    }
  }, [art, def]);

  useEffect(() => {
    void lade();
  }, [lade]);

  if (!def) {
    return (
      <LeitstandRahmen titel="Katalog" benutzer={user?.displayName}>
        <Notiz titel="Unbekannte Objektart">
          Diese Art gibt es im Katalog nicht.{' '}
          <Link to="/einrichten" className="underline">
            Zum Katalog
          </Link>
        </Notiz>
      </LeitstandRahmen>
    );
  }

  const begriff = suchtext.trim().toLowerCase();
  // Kürzel bleiben durchsuchbar (Support tippt "TWA_P25"), aber unsichtbar
  const gefiltert = begriff
    ? objekte.filter(
        (o) => o.name.toLowerCase().includes(begriff) || o.code?.toLowerCase().includes(begriff),
      )
    : objekte;

  return (
    <LeitstandRahmen
      titel={def.mehrzahl}
      benutzer={user?.displayName}
      werkzeuge={
        def.ohneAnlegen ? undefined : (
          <button
            onClick={() => navigate(`/einrichten/${art}/neu`)}
            className="rounded-[4px] border border-akzent bg-akzent px-3 py-1.5 font-zahl text-xs text-akzent-satz"
          >
            {def.einzahl} anlegen
          </button>
        )
      }
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/einrichten" className="text-[13px] text-tinte-3 hover:text-tinte-2">
            ← Katalog
          </Link>
          <input
            type="text"
            value={suchtext}
            onChange={(e) => setSuchtext(e.target.value)}
            placeholder="Filtern …"
            className="min-w-[200px] flex-1 rounded-[4px] border border-linie-2 bg-flaeche px-3 py-1.5 text-sm text-tinte placeholder:text-tinte-3 focus:border-akzent focus:outline-none sm:max-w-xs"
          />
        </div>

        {fehler && (
          <Notiz titel="Konnte nicht laden">
            {fehler}{' '}
            <button onClick={() => void lade()} className="underline">
              Erneut versuchen
            </button>
          </Notiz>
        )}

        {laden && objekte.length === 0 && !fehler && (
          <p className="py-12 text-center text-sm text-tinte-3">Lade {def.mehrzahl} …</p>
        )}

        {!laden && !fehler && (
          <Karte titel={def.mehrzahl} marke={`${gefiltert.length} von ${objekte.length}`} tabelle>
            {gefiltert.length === 0 ? (
              <p className="py-6 text-center text-sm text-tinte-3">
                {objekte.length === 0
                  ? `Noch keine ${def.mehrzahl} angelegt.`
                  : 'Kein Treffer im Filter.'}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Tabelle>
                  <thead>
                    <tr>
                      <Kopfzelle>Name</Kopfzelle>
                      <Kopfzelle>Zustand</Kopfzelle>
                      <Kopfzelle rechts>Geändert</Kopfzelle>
                    </tr>
                  </thead>
                  <tbody>
                    {gefiltert.map((o, i) => {
                      const letzte = i === gefiltert.length - 1;
                      return (
                        <tr
                          key={o.id}
                          onClick={() => navigate(`/einrichten/${art}/${o.id}`)}
                          className={`cursor-pointer hover:bg-flaeche-2 ${o.isActive ? '' : 'opacity-60'}`}
                        >
                          <Zelle letzte={letzte}>{o.name}</Zelle>
                          <Zelle letzte={letzte}>
                            {o.isActive ? (
                              <Zustand art="gut">aktiv</Zustand>
                            ) : (
                              <span className="font-zahl text-[11px] text-tinte-3">
                                außer Betrieb
                              </span>
                            )}
                          </Zelle>
                          <Zelle rechts zahl letzte={letzte}>
                            {new Date(o.updatedAt).toLocaleDateString('de-DE', {
                              timeZone: 'Europe/Berlin',
                              day: '2-digit',
                              month: '2-digit',
                              year: '2-digit',
                            })}
                          </Zelle>
                        </tr>
                      );
                    })}
                  </tbody>
                </Tabelle>
              </div>
            )}
          </Karte>
        )}
      </div>
    </LeitstandRahmen>
  );
}
